import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import AddToTargetListButton from './AddToTargetListButton';
import { getApiAxiosInstance } from '../../../../utils/network';
import { getProfileViews } from '../../../../api';
import useCPPTracking from '../../../hooks/useCPPTracking';
import { CppPlayerActions } from '../../../Analytics/constants';
import styles from '../collegeCoachActivity.scss';

const PAGE_SIZE = 5;
const INITIAL_PAGE = 0;
const TESTID_PREFIX = 'profile.college-coach-activity.coach-views';

export const COLLEGE_COACH_VIEWS_INLINE_PAGE_SIZE = PAGE_SIZE;

const formatDate = (date) => {
    if (!date) {
        return '';
    }
    const parsed = moment(date);
    if (!parsed.isValid()) {
        return '';
    }
    return parsed.format('MMM D, YYYY');
};

const mapViewToRow = (view) => ({
    id: view?.id,
    clubId: view?.clubId,
    clubName: view?.clubName,
    coachFirstName: view?.coachFirstName,
    coachLastName: view?.coachLastName,
    clubProfileImageUrl: view?.clubProfileImages?.thumb || null,
    displayDate: view?.displayViewDate || formatDate(view?.viewedDate),
});

const CollegeCoachViewsTab = ({ playerId, gender, addedClubIds, onAddSuccess, onAddError, onTotalChange }) => {
    const { trackPlayerEngagement } = useCPPTracking();
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [fetchError, setFetchError] = useState(null);

    const handleAddSuccess = (clubId) => {
        trackPlayerEngagement(CppPlayerActions.CPP_COACH_VIEW_ADD_TO_LIST, { schoolId: clubId });
        if (onAddSuccess) {
            onAddSuccess(clubId);
        }
    };

    const fetchRows = useCallback(async () => {
        if (!playerId) {
            return;
        }
        setIsLoading(true);
        setFetchError(null);
        try {
            const response = await getProfileViews(
                getApiAxiosInstance(),
                playerId,
                INITIAL_PAGE,
                PAGE_SIZE,
                gender,
            );
            const views = response?.data?.views || [];
            const totalCount = response?.data?.total || 0;
            setRows(views.map(mapViewToRow));
            setTotal(totalCount);
            if (onTotalChange) {
                onTotalChange(totalCount);
            }
        } catch (err) {
            setFetchError(err?.response?.data?.DisplayMessage || 'We couldn\'t load coach views.');
            setRows([]);
            setTotal(0);
            if (onTotalChange) {
                onTotalChange(0);
            }
        } finally {
            setIsLoading(false);
        }
    }, [playerId, gender, onTotalChange]);

    useEffect(() => {
        fetchRows();
    }, [fetchRows]);

    return (
        <div data-testid={`${TESTID_PREFIX}.container`}>
            {total > 0 && (
                <div
                    className={styles.viewsCount}
                    data-testid={`${TESTID_PREFIX}.views-count`}
                >
                    {`${total} College Coach ${total === 1 ? 'View' : 'Views'}`}
                </div>
            )}
            <div className={styles.tableContainer} data-testid={`${TESTID_PREFIX}.table`}>
                <div className={styles.tableHeader}>
                    <span className={styles.tableHeaderCell} data-testid={`${TESTID_PREFIX}.header.college-name`}>College Name</span>
                    <span className={styles.tableHeaderCell} data-testid={`${TESTID_PREFIX}.header.coach-name`}>Coach Name</span>
                    <span className={styles.tableHeaderCell} data-testid={`${TESTID_PREFIX}.header.date`}>Date</span>
                    <span className={styles.tableHeaderCell} data-testid={`${TESTID_PREFIX}.header.add-to-list`}>Add to List</span>
                </div>
                {isLoading && rows.length === 0 && (
                    <div
                        className={styles.emptyState}
                        data-testid={`${TESTID_PREFIX}.loading`}
                    >
                        Loading…
                    </div>
                )}
                {!isLoading && fetchError && (
                    <div
                        className={styles.errorState}
                        data-testid={`${TESTID_PREFIX}.error`}
                    >
                        {fetchError}
                    </div>
                )}
                {!isLoading && !fetchError && rows.length === 0 && (
                    <div
                        className={styles.emptyState}
                        data-testid={`${TESTID_PREFIX}.empty`}
                    >
                        No College Coach Activity
                    </div>
                )}
                {rows.map((row) => {
                    const rowTestId = `${TESTID_PREFIX}.row-${row.id}`;
                    return (
                        <div key={row.id} className={styles.tableRow} data-testid={rowTestId}>
                            <div className={styles.collegeCell}>
                                {row.clubProfileImageUrl && (
                                    <img
                                        src={row.clubProfileImageUrl}
                                        alt=""
                                        className={styles.collegeLogo}
                                        data-testid={`${rowTestId}.logo`}
                                    />
                                )}
                                <a
                                    href={`/clubs/${row.clubId}`}
                                    className={styles.collegeLink}
                                    data-testid={`${rowTestId}.college-link`}
                                >
                                    {row.clubName}
                                </a>
                            </div>
                            <span
                                className={styles.coachCell}
                                data-testid={`${rowTestId}.coach-name`}
                            >
                                {`${row.coachFirstName || ''} ${row.coachLastName || ''}`.trim()}
                            </span>
                            <span
                                className={styles.dateCell}
                                data-testid={`${rowTestId}.date`}
                            >
                                {row.displayDate}
                            </span>
                            <div className={styles.addCell}>
                                <AddToTargetListButton
                                    clubId={row.clubId}
                                    collegeName={row.clubName}
                                    addedClubIds={addedClubIds}
                                    onSuccess={handleAddSuccess}
                                    onError={onAddError}
                                    data-testid={`${rowTestId}.add-btn`}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

CollegeCoachViewsTab.defaultProps = {
    gender: null,
    addedClubIds: null,
    onAddSuccess: null,
    onAddError: null,
    onTotalChange: null,
};

CollegeCoachViewsTab.propTypes = {
    playerId: PropTypes.number.isRequired,
    gender: PropTypes.string,
    addedClubIds: PropTypes.instanceOf(Set),
    onAddSuccess: PropTypes.func,
    onAddError: PropTypes.func,
    onTotalChange: PropTypes.func,
};

export default CollegeCoachViewsTab;
